import { RootState, AppDispatch } from "../store";
import { useSelector, useDispatch, TypedUseSelectorHook } from "react-redux";
import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import CommonConfig from "../config/common";
import {
  assignRecursive,
  getConfig,
  doTranslation,
  compareState,
  loadTexts,
} from "../utils";
import { addAnimation, setAnimation } from "../store/slices/animations";
import { gsap } from "gsap";
import { PUBLIC_LOCATION } from "../commander/Types";

// creates a custom useSelector with properly set type signature
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = () => useDispatch<AppDispatch>();

const generateId = (prefix = "", suffix = "") => {
  const d = Date.now().toString().substring(6);
  const r = (Math.random() * 10000).toFixed(0);
  return prefix + d + "-" + r + suffix;
};
const getTweenAnimConfig = (config: any) => {
  const exclude = ["main", "end", "atStart", "atEnd"];
  const filtered = Object.keys(config).filter((k) => !exclude.includes(k));
  if (filtered.length <= 1) return null;
  return filtered.reduce((obj: any, key) => {
    obj[key] = config[key];
    return obj;
  }, {});
};

export const useAnimations = (
  config: any,
  name?: string,
  element: RefObject<HTMLElement> | null = null
): [string, (animationName: string) => void] => {
  const dispatch = useAppDispatch();
  const timeOut = useRef(0 as any);
  const [className, setClass] = useState("");
  const id = useRef(name || generateId());
  const animationsState = useAppSelector(
    (state) => state.animations[id.current],
    compareState.bind(null, ["previous", "playing", "className"])
  );
  useEffect(() => {
    dispatch(addAnimation(id.current));

    return () => clearInterval(timeOut.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!animationsState || !animationsState.playing) return;
    if (typeof animationsState.className === "string") {
      setClass(animationsState.className);
    }

    if (element && element.current && animationsState.playing === "none") {
      const props = getTweenAnimConfig(config[animationsState.previous]);
      if (props) {
        gsap.set(element.current, props);
      }
    } else if (element && element.current) {
      const props = getTweenAnimConfig(config[animationsState.playing]);
      if (props) {
        gsap.to(element.current, props);
      }
    }
  }, [animationsState]);

  const playAnimation = (animation: string) => {
    if (!config[animation]) return console.log("no such animation", animation);
    const cfg = config[animation];
    const props = { playing: animation, className: cfg.main || null };
    // NOTE: it is called "atStart/atEnd" in order to be different than the gsap "onStart/onEnd"
    if (cfg.atStart) cfg.atStart();
    dispatch(setAnimation({ name: id.current, props }));

    timeOut.current = setTimeout(() => {
      const className = cfg.main ? cfg.end || "" : null;
      dispatch(
        setAnimation({
          name: id.current,
          props: { previous: animation, playing: "none", className },
        })
      );

      if (cfg.atEnd) cfg.atEnd();
    }, (config[animation].duration + (config[animation].delay ?? 0)) * 1000);
  };

  return [className, playAnimation];
};

export const useTexts = () => {
  const { language } = useAppSelector(
    (state) => state.details,
    compareState.bind(null, ["language"])
  );
  const [texts, setTexts] = useState({ custom: {}, common: {} } as any);

  useEffect(() => {
    if (!language) return;

    const setLang = async () => {
      const common = await loadTexts("common", language);
      const custom = await loadTexts("game", language);

      setTexts({ common, custom });
    };

    setLang();
  }, [language]);

  return texts;
};

export const useTranslation = (key: string, ...values: string[]) => {
  const { custom, common } = useTexts();

  return doTranslation(key, common, custom, ...values);
};

export const useCustomImage = (image?: string, localized = false) => {
  const { language } = useAppSelector(
    (state) => state.details,
    compareState.bind(null, ["gameName", "language"])
  );
  const lang = localized ? language : "en";

  return !!image ? `${PUBLIC_LOCATION}/game/images/${lang}/${image}` : "";
};

export const useConfig = () => {
  const [config, setConfig] = useState({}) as any[];

  useEffect(() => {
    const fetchConfig = async () => {
      const cfg = await getConfig();
      setConfig(cfg);
    };

    fetchConfig();
  }, []);

  return assignRecursive(CommonConfig, config);
};

// some of the buy bonus information /name, description/ is kept in the FE and
// some /multiplier/ is returned from the BE, this hook is used to combined this
// data so it can be easily used from the buy bonus components.
export const useBuyBonusConfig = () => {
  const { buyBonus: FEconfig } = useConfig();
  const { buyBonusState: BEconfig } = useAppSelector(({ buyBonus }) => {
    return {
      buyBonusState: buyBonus,
    };
  });

  const bonuses = Object.keys(BEconfig?.bonuses ?? {}).reduce((obj, bonus) => {
    if (
      FEconfig &&
      FEconfig[bonus] &&
      BEconfig &&
      BEconfig.bonuses[bonus].multiplier > 0
    ) {
      obj[bonus] = {
        ...BEconfig?.bonuses[bonus],
        ...FEconfig[bonus],
      };
    }
    return obj;
  }, {} as any);
  const id = BEconfig?.active ?? null;
  const active = id ? { ...bonuses[id], id } : {};

  return { bonuses, active };
};

export const useLocalStorage = (key: string, defaultValue: string) => {
  const [value, setInitialValue] = useState(() => {
    try {
      const v = window.localStorage.getItem(key);
      if (v) return v;
      window.localStorage.setItem(key, defaultValue);
    } catch (_) {
      return defaultValue;
    }
  });

  const setValue = (newValue: string) => {
    try {
      window.localStorage.setItem(key, newValue);
    } catch (_) {
      setInitialValue(newValue);
    }
  };

  return [value, setValue];
};

export const usePreloadImage = (src: string) => {
  const [sourceLoaded, setSourceLoaded] = useState(null as string | null);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => setSourceLoaded(src);
  }, [src]);

  return sourceLoaded;
};
